import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import {
  Clock, CheckCircle, XCircle, AlertTriangle, RefreshCw,
  Settings, ChevronDown, ChevronUp, Search, Calendar, Users, History, ArrowRight, Plus, Trash2
} from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────────────────────────
const statusBadge = (status, days) => {
  if (status === 'expired') return { label: 'Expired',      bg: 'rgba(239,68,68,0.12)',   border: 'rgba(239,68,68,0.25)',   color: '#f87171',  icon: <XCircle size={12} /> };
  if (days <= 3)            return { label: 'Expiring Soon', bg: 'rgba(245,158,11,0.12)',  border: 'rgba(245,158,11,0.3)',   color: '#fbbf24',  icon: <AlertTriangle size={12} /> };
  return                           { label: 'Active',        bg: 'rgba(16,185,129,0.12)',  border: 'rgba(16,185,129,0.25)',  color: '#34d399',  icon: <CheckCircle size={12} /> };
};

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

// ── Main Component ─────────────────────────────────────────────────────────────
const SubscriptionsManagement = () => {
  const [activeTab, setActiveTab] = useState('list'); // 'list' | 'history'
  const [subs, setSubs]           = useState([]);
  const [history, setHistory]     = useState([]);
  const [config, setConfig]       = useState({ trialDurationDays: 14, maxUsers: 5, features: [] });
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [filter, setFilter]       = useState('all');
  const [configOpen, setConfigOpen] = useState(false);
  
  // Local state for editing config
  const [editConfig, setEditConfig] = useState({ trialDurationDays: 14, maxUsers: 5, features: [] });
  const [newFeature, setNewFeature] = useState('');
  
  const [savingConfig, setSavingConfig] = useState(false);
  const [configMsg, setConfigMsg] = useState('');
  const [adjustRow, setAdjustRow] = useState(null);
  const [adjustDays, setAdjustDays] = useState('7');
  const [adjusting, setAdjusting] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [tenantHistory, setTenantHistory] = useState([]);
  const [viewingHistoryId, setViewingHistoryId] = useState(null);

  const fetchTenantHistory = async (tenantId) => {
    setViewingHistoryId(tenantId);
    try {
      const res = await api(`/master-admin/subscription-history/${tenantId}`);
      if (res.ok) setTenantHistory(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [subsRes, cfgRes, plansRes, histRes] = await Promise.all([
        api('/master-admin/subscriptions'),
        api('/master-admin/subscription-config'),
        api('/master-admin/plans'),
        api('/master-admin/subscription-history')
      ]);
      if (subsRes.ok)  setSubs(await subsRes.json());
      if (cfgRes.ok) { 
        const c = await cfgRes.json(); 
        setConfig(c); 
        setEditConfig({
          trialDurationDays: c.trialDurationDays || 14,
          maxUsers: c.maxUsers || 5,
          features: c.features || []
        }); 
      }
      if (plansRes.ok) setPlans(await plansRes.json());
      if (histRes.ok)  setHistory(await histRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveGlobalConfig = async () => {
    setSavingConfig(true); setConfigMsg('');
    try {
      const res = await api('/master-admin/subscription-config', { 
        method: 'PUT', 
        body: editConfig 
      });
      const d = await res.json();
      if (res.ok) { 
        setConfig(d.config); 
        setConfigMsg('✓ Configuration saved successfully!'); 
        setTimeout(() => setConfigMsg(''), 3000);
      } else {
        setConfigMsg(d.message || 'Error saving');
      }
    } catch { setConfigMsg('Network error'); }
    finally { setSavingConfig(false); }
  };

  const addFeature = () => {
    if (!newFeature.trim()) return;
    setEditConfig(prev => ({ ...prev, features: [...prev.features, newFeature.trim()] }));
    setNewFeature('');
  };

  const removeFeature = (idx) => {
    setEditConfig(prev => ({ ...prev, features: prev.features.filter((_, i) => i !== idx) }));
  };

  const handleAdjust = async (tenantId) => {
    if (adjustDays === undefined || isNaN(adjustDays) || adjustDays === '') return;
    setAdjusting(true);
    try {
      const res = await api(`/master-admin/adjust`, { method: 'PUT', body: { tenantId, daysToAdd: parseInt(adjustDays, 10) } });
      if (res.ok) { setAdjustRow(null); await fetchAll(); }
      else { const d = await res.json(); alert(d.message || 'Failed to adjust'); }
    } catch (e) { alert(e.message); }
    finally { setAdjusting(false); }
  };

  const handleAssignPlan = async (tenantId) => {
    if (!selectedPlanId) return;
    setAdjusting(true);
    try {
      const res = await api('/master-admin/assign-plan', { method: 'PUT', body: { tenantId, planId: selectedPlanId } });
      if (res.ok) { setAdjustRow(null); setSelectedPlanId(''); await fetchAll(); }
      else { const d = await res.json(); alert(d.message || 'Error assigning'); }
    } catch (e) { alert(e.message); }
    finally { setAdjusting(false); }
  };

  const filtered = subs.filter(s => {
    const q = search.toLowerCase();
    const matchSearch = !q || s.admin?.companyName?.toLowerCase().includes(q) || s.admin?.email?.toLowerCase().includes(q) || s.admin?.name?.toLowerCase().includes(q);
    const matchFilter = filter === 'all' ? true : filter === 'active' ? (s.status === 'active' && s.daysRemaining > 3) : filter === 'expiring' ? (s.status === 'active' && s.daysRemaining <= 3) : filter === 'expired' ? s.status === 'expired' : true;
    return matchSearch && matchFilter;
  });

  const cell = { padding: '14px 16px', fontSize: '13px', color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.04)', verticalAlign: 'middle' };
  const th   = { padding: '11px 16px', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(0,0,0,0.2)', textAlign: 'left' };

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", color: '#f8fafc' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '800', letterSpacing: '-0.02em', marginBottom: '6px' }}>Subscription Management</h1>
          <p style={{ color: '#64748b', fontSize: '14px' }}>Control your platform's trials, user limits, and track plan history.</p>
        </div>
        <div style={{ display: 'flex', background: 'rgba(255,255,255,0.05)', padding: '4px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)' }}>
          <button onClick={() => setActiveTab('list')} style={{ padding: '8px 16px', borderRadius: '10px', fontSize: '13px', fontWeight: '600', border: 'none', background: activeTab === 'list' ? '#3b82f6' : 'transparent', color: activeTab === 'list' ? 'white' : '#64748b', cursor: 'pointer' }}>Tenants</button>
          <button onClick={() => setActiveTab('history')} style={{ padding: '8px 16px', borderRadius: '10px', fontSize: '13px', fontWeight: '600', border: 'none', background: activeTab === 'history' ? '#3b82f6' : 'transparent', color: activeTab === 'history' ? 'white' : '#64748b', cursor: 'pointer' }}>Plan History</button>
        </div>
      </div>

      {activeTab === 'list' && (
        <div style={{ background: 'rgba(59,130,246,0.03)', border: '1px solid rgba(59,130,246,0.1)', borderRadius: '16px', marginBottom: '24px', overflow: 'hidden' }}>
          <button onClick={() => setConfigOpen(!configOpen)} style={{ width: '100%', padding: '16px 20px', background: 'transparent', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(59,130,246,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Settings size={18} color="#60a5fa" />
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: '15px', fontWeight: '700', color: '#f8fafc' }}>Free Trial Global Configuration</div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>{config.trialDurationDays} Days · {config.maxUsers} Users · {config.features?.length || 0} Features</div>
              </div>
            </div>
            {configOpen ? <ChevronUp size={18} color="#64748b" /> : <ChevronDown size={18} color="#64748b" />}
          </button>
          
          {configOpen && (
            <div style={{ padding: '24px', borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#94a3b8', marginBottom: '8px' }}>Trial Duration (Days)</label>
                  <input type="number" value={editConfig.trialDurationDays} onChange={e=>setEditConfig({...editConfig, trialDurationDays: parseInt(e.target.value)})} style={{ width: '100%', padding: '10px', borderRadius: '10px', background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(255,255,255,0.1)', color: 'white' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#94a3b8', marginBottom: '8px' }}>User Limit (Seats)</label>
                  <input type="number" value={editConfig.maxUsers} onChange={e=>setEditConfig({...editConfig, maxUsers: parseInt(e.target.value)})} style={{ width: '100%', padding: '10px', borderRadius: '10px', background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(255,255,255,0.1)', color: 'white' }} />
                </div>
              </div>
              
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#94a3b8', marginBottom: '8px' }}>Trial Features Displayed</label>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                  <input placeholder="Add a feature (e.g. Lead Export)..." value={newFeature} onChange={e=>setNewFeature(e.target.value)} onKeyPress={e=>e.key==='Enter' && addFeature()} style={{ flex: 1, padding: '10px', borderRadius: '10px', background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(255,255,255,0.1)', color: 'white' }} />
                  <button onClick={addFeature} style={{ padding: '0 16px', background: '#3b82f6', border: 'none', borderRadius: '10px', color: 'white', cursor: 'pointer' }}><Plus size={18} /></button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {editConfig.features.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.05)', padding: '6px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)', fontSize: '12px' }}>
                      {f} <Trash2 size={14} color="#f87171" onClick={()=>removeFeature(i)} style={{ cursor: 'pointer' }} />
                    </div>
                  ))}
                </div>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '20px' }}>
                <div style={{ fontSize: '13px', color: configMsg.includes('✓') ? '#34d399' : '#64748b' }}>{configMsg || 'These settings apply to all new trial registrations.'}</div>
                <button onClick={saveGlobalConfig} disabled={savingConfig} style={{ padding: '10px 24px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '700', cursor: 'pointer' }}>{savingConfig ? 'Saving...' : 'Save Configuration'}</button>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'list' ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
            {[{ l: 'Total Tenants', v: subs.length, c: '#3b82f6' }, { l: 'Active', v: subs.filter(s=>s.status==='active').length, c: '#10b981' }, { l: 'Expiring', v: subs.filter(s=>s.daysRemaining<=3).length, c: '#f59e0b' }, { l: 'Expired', v: subs.filter(s=>s.status==='expired').length, c: '#ef4444' }].map((c, i) => (
              <div key={i} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '16px', padding: '20px' }}>
                <div style={{ fontSize: '24px', fontWeight: '800', color: c.c }}>{c.v}</div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>{c.l}</div>
              </div>
            ))}
          </div>

          <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', gap: '12px' }}>
              <Search size={16} color="#475569" />
              <input placeholder="Search..." value={search} onChange={e=>setSearch(e.target.value)} style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'white', fontSize: '14px' }} />
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Company</th><th style={th}>Plan</th><th style={th}>Status</th><th style={th}>Expires</th><th style={th}>Actions</th></tr></thead>
                <tbody>
                  {filtered.map(sub => (
                    <React.Fragment key={sub._id}>
                      <tr>
                        <td style={cell}>
                          <div style={{ fontWeight: '700', color: '#f8fafc' }}>{sub.admin?.companyName || 'N/A'}</div>
                          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{sub.admin?.name} • {sub.admin?.phone || 'No Phone'}</div>
                          <div style={{ fontSize: '11px', color: '#475569' }}>{sub.admin?.email}</div>
                        </td>
                        <td style={cell}>{sub.plan === 'free_trial' ? 'Trial' : sub.plan}</td>
                        <td style={cell}><span style={{ color: sub.status==='active' ? '#34d399' : '#f87171' }}>{sub.status}</span></td>
                        <td style={cell}>{fmt(sub.trialEndDate)}</td>
                        <td style={cell}>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={() => setAdjustRow(adjustRow === sub.tenantId ? null : sub.tenantId)} style={{ padding: '6px 12px', borderRadius: '6px', fontSize: '12px', background: 'rgba(59,130,246,0.1)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.2)', cursor: 'pointer' }}>Adjust/Assign</button>
                            <button onClick={() => fetchTenantHistory(sub.tenantId)} style={{ padding: '6px 12px', borderRadius: '6px', fontSize: '12px', background: 'rgba(255,255,255,0.05)', color: '#94a3b8', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}><History size={12} /> Timeline</button>
                          </div>
                        </td>
                      </tr>
                      {adjustRow === sub.tenantId && (
                        <tr>
                          <td colSpan={5} style={{ background: 'rgba(59,130,246,0.03)', padding: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
                               <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                 <span style={{ fontSize: '13px', color: '#64748b' }}>Adjust Days:</span>
                                 <input type="number" value={adjustDays} onChange={e=>setAdjustDays(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', width: '70px', border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(15,23,42,0.7)', color: 'white', fontSize: '13px', outline: 'none' }} />
                                 <button onClick={() => handleAdjust(sub.tenantId)} disabled={adjusting} style={{ padding: '8px 16px', borderRadius: '8px', background: 'rgba(59,130,246,0.15)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.3)', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>{adjusting ? '...' : 'Adjust'}</button>
                               </div>
                               <div style={{ width: '1px', height: '24px', background: 'rgba(255,255,255,0.1)' }}></div>
                               <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                 <span style={{ fontSize: '13px', color: '#64748b' }}>Assign Plan:</span>
                                 <select value={selectedPlanId} onChange={e=>setSelectedPlanId(e.target.value)} style={{ background: '#0f172a', color: 'white', padding: '8px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.12)', fontSize: '13px' }}>
                                   <option value="">Select Plan</option>
                                   {plans.filter(p => !p.isCustom).map(p => <option key={p._id} value={p._id}>{p.name} — ₹{p.price}/{p.durationDays}d</option>)}
                                 </select>
                                 <button onClick={() => handleAssignPlan(sub.tenantId)} disabled={adjusting || !selectedPlanId} style={{ background: '#3b82f6', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>{adjusting ? '...' : 'Assign'}</button>
                               </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Timeline Modal/Overlay ───────────────────────────────────────── */}
          {viewingHistoryId && (
            <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
              <div style={{ width: '100%', maxWidth: '800px', maxHeight: '100%', background: '#0f172a', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)' }}>
                <div style={{ padding: '24px', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <h2 style={{ fontSize: '18px', fontWeight: '800' }}>Subscription Timeline</h2>
                    <p style={{ color: '#64748b', fontSize: '13px' }}>Full audit trail for this tenant</p>
                  </div>
                  <button onClick={() => setViewingHistoryId(null)} style={{ background: 'rgba(255,255,255,0.05)', border: 'none', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white' }}><XCircle size={20} /></button>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
                  {tenantHistory.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#475569', padding: '40px' }}>No history records found for this tenant.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                      {tenantHistory.map((h, i) => (
                        <div key={h._id} style={{ display: 'flex', gap: '24px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '20px' }}>
                            <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: h.action === 'custom_assign' ? '#f59e0b' : '#3b82f6', border: '3px solid rgba(255,255,255,0.1)', zIndex: 1 }} />
                            {i !== tenantHistory.length - 1 && <div style={{ width: '2px', flex: 1, background: 'rgba(255,255,255,0.05)', margin: '4px 0' }} />}
                          </div>
                          <div style={{ flex: 1, paddingBottom: '32px' }}>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', marginBottom: '4px' }}>{fmtTime(h.createdAt)}</div>
                            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '12px', padding: '16px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                <span style={{ textTransform: 'uppercase', fontSize: '10px', fontWeight: '800', background: h.action === 'custom_assign' ? 'rgba(245,158,11,0.1)' : 'rgba(59,130,246,0.1)', color: h.action === 'custom_assign' ? '#f59e0b' : '#3b82f6', padding: '2px 8px', borderRadius: '4px' }}>{h.action.replace('_', ' ')}</span>
                                <div style={{ flex: 1 }} />
                                <div style={{ fontSize: '11px', color: '#475569' }}>by {h.performedBy}</div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '15px', fontWeight: '700', color: '#f8fafc', marginBottom: '8px' }}>
                                <span style={{ color: '#475569' }}>{h.oldPlan || 'START'}</span>
                                <ArrowRight size={14} color="#334155" />
                                <span>{h.newPlan}</span>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', borderTop: '1px solid rgba(255,255,255,0.03)', paddingTop: '12px' }}>
                                <div>
                                  <div style={{ fontSize: '11px', color: '#475569', textTransform: 'uppercase' }}>Seat Limit</div>
                                  <div style={{ fontSize: '13px', color: '#94a3b8' }}>{h.maxUsers} Users</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: '11px', color: '#475569', textTransform: 'uppercase' }}>Expiry Date</div>
                                  <div style={{ fontSize: '13px', color: '#94a3b8' }}>{fmt(h.endDate)}</div>
                                </div>
                              </div>
                              {h.notes && <div style={{ marginTop: '12px', fontSize: '12px', color: '#64748b', background: 'rgba(0,0,0,0.2)', padding: '8px 12px', borderRadius: '8px', borderLeft: '3px solid #334155' }}>{h.notes}</div>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '16px', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', gap: '12px' }}>
            <Search size={16} color="#475569" />
            <input placeholder="Search history by company..." value={search} onChange={e=>setSearch(e.target.value)} style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'white', fontSize: '14px' }} />
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr><th style={th}>Date & Time</th><th style={th}>Company</th><th style={th}>Action</th><th style={th}>Plan Change</th><th style={th}>Details</th><th style={th}>Performed By</th></tr>
              </thead>
              <tbody>
                {history.filter(h => !search || h.superAdminId?.companyName?.toLowerCase().includes(search.toLowerCase())).map(h => (
                  <tr key={h._id}>
                    <td style={cell}>{fmtTime(h.createdAt)}</td>
                    <td style={cell}>{h.superAdminId?.companyName || '—'}</td>
                    <td style={cell}><span style={{ textTransform: 'uppercase', fontSize: '10px', fontWeight: '800', padding: '2px 8px', borderRadius: '100px', background: h.action==='custom_assign' ? 'rgba(245,158,11,0.1)' : 'rgba(59,130,246,0.1)', color: h.action==='custom_assign' ? '#f59e0b' : '#3b82f6' }}>{h.action.replace('_', ' ')}</span></td>
                    <td style={cell}><div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ color: '#475569' }}>{h.oldPlan || 'None'}</span><ArrowRight size={12} color="#475569" /><span style={{ color: '#f8fafc', fontWeight: '600' }}>{h.newPlan}</span></div></td>
                    <td style={cell}><div style={{ fontSize: '11px', color: '#64748b' }}>{h.maxUsers} Users · Expires {fmt(h.endDate)}</div></td>
                    <td style={cell}>{h.performedBy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionsManagement;
